import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { adminClient, setAccessTokenProvider } from '@/api/adminClient';
import { AUTH_AUDIENCE, AUTH_CONFIGURED, useAuthSession } from '@/auth';
import { setOpsAccessTokenProvider } from '@/hooks/useOpsSnapshot';
import {
  type AdminAccess,
  NO_ACCESS,
  NO_STUDIO_ROLES,
  type StudioRoleAccess,
  adminAccessFromPermissions,
  hasAnyAdminAccess,
  permissionsFromToken,
  studioRolesFromPermissions,
} from '@/lib/platformAdmin';

const API_AUDIENCE = AUTH_AUDIENCE;
const TOKEN_REFRESH_SKEW_SECONDS = 60;
/** How often a signed-in tab re-asks the API, so a role change reaches an open Studio Admin. */
const PERMISSION_REFRESH_MS = 60_000;
/** Local dev and an unconfigured build run without identity-provider roles, so they see everything. */
const LOCAL_FULL_ACCESS = !AUTH_CONFIGURED || import.meta.env.VITE_APP_ENV === 'local';

function decodeJwtPayload(token: string): { exp?: number } | null {
  const [, payload] = token.split('.');
  if (!payload) return null;
  try {
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), '=');
    return JSON.parse(globalThis.atob(padded)) as { exp?: number };
  } catch {
    return null;
  }
}

function isExpiredOrExpiringSoon(token: string): boolean {
  const payload = decodeJwtPayload(token);
  if (!payload?.exp) return false;
  return payload.exp <= Math.floor(Date.now() / 1000) + TOKEN_REFRESH_SKEW_SECONDS;
}

function samePermissions(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((permission, index) => permission === b[index]);
}

export function useAuth() {
  const { isAuthenticated, isLoading, user, loginWithRedirect, logout, getAccessTokenSilently } =
    useAuthSession();
  const [tokenPermissions, setTokenPermissionsState] = useState<string[]>([]);
  const [apiPermissions, setApiPermissions] = useState<string[]>([]);
  const [tokenResolved, setTokenResolved] = useState(false);
  const [apiResolved, setApiResolved] = useState(false);
  // The signed-out branch used to mark the permission check resolved. Auth
  // then flips to signed-in a render later, and that stale "resolved, not an
  // admin" pair paints Access denied until the token check catches up.
  const [permissionForAuthenticated, setPermissionForAuthenticated] = useState(isAuthenticated);
  if (permissionForAuthenticated !== isAuthenticated) {
    setPermissionForAuthenticated(isAuthenticated);
    setTokenPermissionsState([]);
    setApiPermissions([]);
    setTokenResolved(false);
    setApiResolved(false);
  }
  const providerSetRef = useRef(false);

  const setTokenPermissions = useCallback((next: string[]) => {
    setTokenPermissionsState((current) => (samePermissions(current, next) ? current : next));
    setTokenResolved(true);
  }, []);

  if (!providerSetRef.current) {
    providerSetRef.current = true;
    const tokenProvider = async () => {
      const authorizationParams = API_AUDIENCE ? { audience: API_AUDIENCE } : undefined;
      try {
        let token = await getAccessTokenSilently({ authorizationParams });
        if (isExpiredOrExpiringSoon(token)) {
          token = await getAccessTokenSilently({
            cacheMode: 'off',
            authorizationParams,
          });
        }
        setTokenPermissions(permissionsFromToken(token));
        return token;
      } catch (error) {
        console.warn('[Studio Admin] Failed to get access token:', error);
        setTokenPermissions([]);
        return '';
      }
    };
    setAccessTokenProvider(tokenProvider);
    setOpsAccessTokenProvider(tokenProvider);
  }

  // Studio roles are tenant roles the API resolves itself, so the token is
  // only the fast path: what the API says the caller holds is the answer, and
  // a role given in Users shows here without signing out.
  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    const authorizationParams = API_AUDIENCE ? { audience: API_AUDIENCE } : undefined;
    const resolve = async () => {
      let token: string;
      try {
        token = await getAccessTokenSilently({ authorizationParams });
      } catch {
        if (!cancelled) {
          setTokenPermissions([]);
          setApiResolved(true);
        }
        return;
      }
      if (cancelled) return;
      setTokenPermissions(permissionsFromToken(token));
      try {
        const me = await adminClient.getMe(token);
        if (!cancelled) {
          const next = Array.isArray(me.permissions) ? me.permissions.filter((permission) => typeof permission === 'string') : [];
          setApiPermissions((current) => (samePermissions(current, next) ? current : next));
        }
      } catch (error) {
        console.warn('[Studio Admin] Failed to read your permissions from the Studio API:', error);
      } finally {
        if (!cancelled) setApiResolved(true);
      }
    };
    void resolve();
    const interval = setInterval(() => void resolve(), PERMISSION_REFRESH_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') void resolve();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [getAccessTokenSilently, isAuthenticated, setTokenPermissions]);

  useEffect(() => {
    return () => {
      providerSetRef.current = false;
    };
  }, []);

  const permissions = useMemo(() => [...new Set([...tokenPermissions, ...apiPermissions])], [tokenPermissions, apiPermissions]);
  const access: AdminAccess = useMemo(() => (permissions.length > 0 ? adminAccessFromPermissions(permissions) : NO_ACCESS), [permissions]);
  const studioRoles: StudioRoleAccess = useMemo(
    () => (permissions.length > 0 ? studioRolesFromPermissions(permissions) : NO_STUDIO_ROLES),
    [permissions],
  );
  const permissionResolved = tokenResolved && (apiResolved || hasAnyAdminAccess(access));

  const isPlatformAdmin = LOCAL_FULL_ACCESS || access.isPlatformAdmin;
  const isStudioAdmin = isPlatformAdmin || access.isStudioAdmin;
  const canBuildIntegrations = isPlatformAdmin || access.canBuildIntegrations;
  const canApproveIntegrations = isPlatformAdmin || access.canApproveIntegrations;
  const canCurateTemplates = isPlatformAdmin || access.canCurateTemplates;
  const canEditCode = isPlatformAdmin || studioRoles.canEditCode;
  const canImportRepos = isPlatformAdmin || studioRoles.canImportRepos;
  const canSeeInternal = isPlatformAdmin || studioRoles.canSeeInternal;
  const canCurateSkills = isPlatformAdmin || access.canCurateSkills;

  return {
    isAuthenticated,
    isLoading,
    user,
    loginWithRedirect,
    logout,
    isPlatformAdmin,
    isStudioAdmin,
    canBuildIntegrations,
    canApproveIntegrations,
    canCurateTemplates,
    canEditCode,
    canImportRepos,
    canSeeInternal,
    canCurateSkills,
    permissionResolved,
  };
}
